import {
  IOvertimeAttachmentParams,
  IFetchOvertimeRequestsFilterParams,
  IOvertimeRequestActions,
  IUpdateOvertimeStatePayload,
  OvertimeContext,
  IOvertimeCommentCommentFetchActionPayload,
  IMentionAttachmentCommentUpdateActionPayload,
  IMentionAttachmentCommentAttachmentRemoveActionPayload,
  IMentionAttachmentCommentAttachmentAddActionPayload
} from './actions.types';
import {
  IMentionAttachmentCommentData,
  IOvertimeRequestData,
  IUserData
} from '@/models';
import { IOvertimeRequestPaginatedListData } from '@/services/overtime-request';
import { ITransition } from '@/models/transition';

export const actions: IOvertimeRequestActions = {
  fetchOvertimeRequests(
    context: OvertimeContext,
    payload: IFetchOvertimeRequestsFilterParams
  ): Promise<void> {
    const promise = this.$services.overtimeRequest.fetchOvertimeRequests(
      payload
    );
    context.commit('SET_LAST_PROMISE', promise);
    return promise.then(
      (overtimeRequests: IOvertimeRequestPaginatedListData) => {
        if (promise == context.state.lastPromise) {
          context.commit('SET_COUNT', overtimeRequests.count);
          context.commit('SET_OVERTIME_REQUESTS', overtimeRequests.results);
        }
        context.commit('SET_LAST_PROMISE', null);
      }
    );
  },

  fetchOvertimeRequest(
    context: OvertimeContext,
    payload: number
  ): Promise<void> {
    return this.$services.overtimeRequest
      .fetchOvertimeRequest(payload)
      .then((overtimeRequest: IOvertimeRequestData) => {
        const index = context.state.overtimeRequests.findIndex(
          (v: IOvertimeRequestData) => v.id === overtimeRequest.id
        );
        if (index === -1) {
          context.commit('ADD_OVERTIME_REQUEST', overtimeRequest);
        } else {
          context.commit('UPDATE_OVERTIME_REQUEST', overtimeRequest);
        }
      });
  },

  createOvertimeRequest(
    context: OvertimeContext,
    payload: IOvertimeRequestData
  ): Promise<void> {
    return this.$services.overtimeRequest
      .createOvertimeRequest(payload)
      .then((overtimeRequest: IOvertimeRequestData) =>
        context.commit('ADD_OVERTIME_REQUEST', overtimeRequest)
      );
  },

  updateOvertimeRequest(
    context: OvertimeContext,
    payload: IOvertimeRequestData
  ): Promise<void> {
    return this.$services.overtimeRequest
      .updateOvertimeRequest(payload)
      .then((overtimeRequest: IOvertimeRequestData) => {
        const index = context.state.overtimeRequests.findIndex(
          (v: IOvertimeRequestData) => v.id === overtimeRequest.id
        );
        if (index === -1) {
          context.commit('ADD_OVERTIME_REQUEST', overtimeRequest);
        } else {
          context.commit('UPDATE_OVERTIME_REQUEST', overtimeRequest);
        }
      });
  },

  deleteOvertimeRequest(
    context: OvertimeContext,
    payload: number
  ): Promise<void> {
    return this.$services.overtimeRequest
      .deleteOvertimeRequest(payload)
      .then(() => {
        context.commit('REMOVE_OVERTIME_REQUEST', payload);
      });
  },

  updateOvertimeRequestState: function(
    context: OvertimeContext,
    payload: IUpdateOvertimeStatePayload
  ): Promise<void> {
    return this.$services.overtimeRequest
      .updateOvertimeRequestState(payload)
      .then((overtimeRequest: IOvertimeRequestData) => {
        const index = context.state.overtimeRequests.findIndex(
          (v: IOvertimeRequestData) => v.id === overtimeRequest.id
        );
        if (index === -1) {
          context.commit('ADD_OVERTIME_REQUEST', overtimeRequest);
        } else {
          context.commit('UPDATE_OVERTIME_REQUEST', overtimeRequest);
        }
      });
  },

  fetchOvertimeRequestActivities(
    context: OvertimeContext,
    payload: number
  ): Promise<void> {
    return this.$services.overtimeRequest
      .fetchOvertimeRequestActivities(payload)
      .then((activities: ITransition[]) => {
        context.commit('SET_OVERTIME_REQUEST_ACTIVITIES', activities);
      });
  },

  fetchOvertimeRequestComments(
    context: OvertimeContext,
    payload: number
  ): Promise<void> {
    return this.$services.overtimeRequest
      .fetchOvertimeRequestComments(payload)
      .then((comments: IMentionAttachmentCommentData[]) => {
        context.commit('SET_OVERTIME_REQUEST_COMMENTS', comments);
      });
  },

  fetchOvertimeRequestComment(
    context: OvertimeContext,
    payload: IOvertimeCommentCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.overtimeRequest
      .fetchOvertimeRequestComment(payload.overtimeId, payload.commentId)
      .then((comment: IMentionAttachmentCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_OVERTIME_REQUEST_COMMENT', comment);
        } else {
          context.commit('UPDATE_OVERTIME_REQUEST_COMMENT', comment);
        }
      });
  },

  createOvertimeRequestComment(
    context: OvertimeContext,
    payload: IMentionAttachmentCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.overtimeRequest
      .createOvertimeRequestComment(payload.overtimeId, payload.comment)
      .then((comment: IMentionAttachmentCommentData) => {
        context.commit(
          'SET_LAST_CREATED_OVERTIME_REQUEST_COMMENT_ID',
          comment.id
        );
        context.commit('ADD_OVERTIME_REQUEST_COMMENT', comment);
      });
  },

  updateOvertimeRequestComment(
    context: OvertimeContext,
    payload: IMentionAttachmentCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.overtimeRequest
      .updateOvertimeRequestComment(payload.overtimeId, payload.comment)
      .then((comment: IMentionAttachmentCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_OVERTIME_REQUEST_COMMENT', comment);
        } else {
          context.commit('UPDATE_OVERTIME_REQUEST_COMMENT', comment);
        }
      });
  },

  deleteOvertimeRequestComment(
    context: OvertimeContext,
    payload: IOvertimeCommentCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.overtimeRequest
      .deleteOvertimeRequestComment(payload.overtimeId, payload.commentId)
      .then(() => {
        context.commit('REMOVE_OVERTIME_REQUEST_COMMENT', payload.commentId);
      });
  },

  addOvertimeRequestCommentAttachments(
    context: OvertimeContext,
    payload: IMentionAttachmentCommentAttachmentAddActionPayload
  ): Promise<void> {
    return this.$services.overtimeRequest
      .addOvertimeRequestCommentAttachments(
        payload.overtimeId,
        payload.commentId,
        payload.attachments
      )
      .then((comment: IMentionAttachmentCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );
        if (index === -1) {
          context.commit('ADD_OVERTIME_REQUEST_COMMENT', comment);
        } else {
          context.commit('UPDATE_OVERTIME_REQUEST_COMMENT', comment);
        }
      });
  },

  removeOvertimeRequestCommentAttachment(
    context: OvertimeContext,
    payload: IMentionAttachmentCommentAttachmentRemoveActionPayload
  ): Promise<void> {
    return this.$services.overtimeRequest
      .removeOvertimeRequestCommentAttachment(
        payload.overtimeId,
        payload.commentId,
        payload.attachmentId
      )
      .then(() => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );

        if (index !== -1) {
          const comment = context.state.comments[index];

          const attachmentIndex = comment.attachments!.findIndex(
            attachment => attachment.id === payload.attachmentId
          );

          comment.attachments!.splice(attachmentIndex, 1);

          context.commit('UPDATE_OVERTIME_REQUEST_COMMENT', comment);
        }
      });
  },

  // Mentions
  fetchOvertimeRequestCommentsMentionableUsers(
    context: OvertimeContext,
    payload: number
  ): Promise<void> {
    return this.$services.overtimeRequest
      .fetchOvertimeRequestCommentsMentionableUsers(payload)
      .then((mentionableUsers: IUserData[]) => {
        context.commit('SET_MENTIONABLE_USERS', mentionableUsers);
      });
  },

  downloadOvertimeAttachment(
    _context: OvertimeContext,
    payload: IOvertimeAttachmentParams
  ): Promise<Blob> {
    return this.$services.overtimeRequest
      .downloadOvertimeAttachment(payload.id, payload.attachmentId)
      .then(data => data);
  },

  deleteOvertimeAttachment(
    _context: OvertimeContext,
    payload: IOvertimeAttachmentParams
  ): Promise<Blob> {
    return this.$services.overtimeRequest
      .deleteOvertimeAttachment(payload.id, payload.attachmentId)
      .then(data => data);
  }
};
